import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Edit2, Plus, Trash2 } from "lucide-react";
import AppModal from "../../components/AppModal";
import DataTable from "../../components/DataTable";
import ResourcePage from "../../components/ResourcePage";
import { api } from "../../axios/api";
import { confirmAction, notify } from "../../utils/toastAlert";

const emptyForm = {
  nome: "",
  sigla: "",
  iban: "",
  conta: "",
  swift: "",
  status: "activo",
};

const closeModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).hide();
};

const showModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).show();
};

const Bancos = () => {
  const user = useSelector((state) => state.user.user);
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);

  const fetchBancos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/bancos");
      const allBancos = response.data?.data || response.data || [];
      const companyBancos = allBancos.filter(
        (item) => Number(item.empresa_id) === Number(user?.empresa_id)
      );
      setBancos(companyBancos);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao carregar bancos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.empresa_id) {
      fetchBancos();
    }
  }, [user?.empresa_id]);

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const openEdit = (item = selected) => {
    if (!item) return;
    setEditing(item);
    setForm({
      nome: item.nome || "",
      sigla: item.sigla || "",
      iban: item.iban || "",
      conta: item.conta || "",
      swift: item.swift || "",
      status: item.status || "activo",
    });
    showModal("bancoFormModal");
  };

  const payload = () => {
    const data = { ...form };
    if (user?.empresa_id) {
      data.empresa_id = user.empresa_id;
    }
    return data;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      if (editing) {
        await api.post(`/bancos/${editing.id}`, payload());
        await notify("Banco actualizado com sucesso.", "success");
      } else {
        await api.post("/bancos", payload());
        await notify("Banco cadastrado com sucesso.", "success");
      }

      setForm(emptyForm);
      setEditing(null);
      closeModal("bancoFormModal");
      fetchBancos();
    } catch (err) {
      await notify(err.response?.data?.message || "Não foi possível guardar o banco.", "error");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!selected) return;
    const question = await confirmAction({
      title: "Excluir banco",
      text: `Deseja excluir o banco ${selected.nome}?`,
      icon: "warning",
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });

    if (!question.isConfirmed) return;

    try {
      setLoading(true);
      await api.delete(`/bancos/${selected.id}`);
      await notify("Banco excluído com sucesso.", "success");
      setSelected(null);
      fetchBancos();
    } catch (err) {
      await notify(err.response?.data?.message || "Não foi possível excluir o banco.", "error");
    } finally {
      setLoading(false);
    }
  };

  const canManage = user?.nivel === "admin";

  return (
    <ResourcePage
      title="Bancos"
      breadcrumb={["Gestão", "Bancos"]}
      actions={(
        <>
          <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" type="button" data-bs-toggle="modal" data-bs-target="#bancoFormModal" onClick={openCreate} disabled={!canManage}>
            <Plus size={16} />
            Cadastrar
          </button>
          <button className="btn btn-light btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={() => openEdit()} disabled={!canManage || !selected}>
            <Edit2 size={16} />
            Editar
          </button>
          <button className="btn btn-light text-danger btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={confirmDelete} disabled={!canManage || !selected}>
            <Trash2 size={16} />
            Excluir
          </button>
        </>
      )}
    >
      <div className="card app-card bg-white">
        <div className="card-body">
          {error && <div className="alert alert-danger py-2">{error}</div>}
          {!canManage && <div className="alert alert-warning py-2">Apenas administradores podem gerir bancos.</div>}

          <DataTable
            id="bancosDataTable"
            columns={["Nome", "Sigla", "IBAN", "Conta", "SWIFT", "Estado"]}
            rows={bancos}
            renderRow={(item) => (
              <tr key={item.id} className={selected?.id === item.id ? "table-active" : ""} onClick={() => setSelected(item)}>
                <td>{item.nome}</td>
                <td>{item.sigla || "-"}</td>
                <td>{item.iban || "-"}</td>
                <td>{item.conta || "-"}</td>
                <td>{item.swift || "-"}</td>
                <td><span className={`badge ${item.status === "activo" ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}>{item.status}</span></td>
              </tr>
            )}
          />
        </div>
      </div>

      <AppModal
        id="bancoFormModal"
        title={editing ? "Editar banco" : "Cadastrar banco"}
        footer={(
          <>
            <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <button type="submit" className="btn btn-primary" form="bancoForm" disabled={loading || !canManage}>
              {loading ? "A guardar..." : "Guardar"}
            </button>
          </>
        )}
      >
        <form id="bancoForm" onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-8 mb-3">
              <label className="form-label" htmlFor="nome">Nome</label>
              <input className="form-control" id="nome" name="nome" value={form.nome} onChange={handleChange} required />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label" htmlFor="sigla">Sigla</label>
              <input className="form-control" id="sigla" name="sigla" value={form.sigla} onChange={handleChange} />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="iban">IBAN</label>
              <input className="form-control" id="iban" name="iban" value={form.iban} onChange={handleChange} />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="conta">Conta</label>
              <input className="form-control" id="conta" name="conta" value={form.conta} onChange={handleChange} />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="swift">SWIFT</label>
              <input className="form-control" id="swift" name="swift" value={form.swift} onChange={handleChange} />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="status">Estado</label>
              <select className="form-select" id="status" name="status" value={form.status} onChange={handleChange}>
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </select>
            </div>
          </div>
        </form>
      </AppModal>
    </ResourcePage>
  );
};

export default Bancos;
